import React, { useEffect, useState } from "react";
import { parse } from "papaparse";
import { Card, Flex, Text, FileIcon } from "@jsq/ds/core";
import { LoadingPage } from "~/components/LoadingPage/LoadingPage";
import { ComplianceDocumentType } from "../../../DocumentManagementList/types";
import {
  csvCardErrorStyle,
  csvCardStyle,
  csvTableBodyStyle,
  csvTableHeaderStyle,
  csvTableRowStyle,
  csvTableStyle,
} from "./style_vanilla.css";

type Props = {
  document: ComplianceDocumentType | undefined;
};

export const CsvViewPanel: React.FunctionComponent<Props> = ({ document }) => {
  const [csvData, setCsvData] = useState<string[][] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!document?.contentType?.includes("csv") || !document?.s3Link) {
      setCsvData(null);
      return;
    }

    const controller = new AbortController();

    const fetchCsvData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(document.s3Link, { signal: controller.signal });
        const csvText = await response.text();

        /**
         * Parses the provided CSV text using the specified configuration.
         *
         * Configuration options:
         * - `header: false` - Indicates that the CSV data does not include a header row.
         * - `skipEmptyLines: true` - Skips any empty lines in the CSV data to ensure cleaner parsing.
         *
         * @param csvText - The raw CSV text to be parsed.
         * @returns The parsed CSV data as an object or array, depending on the parser's implementation.
         */
        const parsed = parse(csvText, {
          header: false,
          skipEmptyLines: true,
        });

        if (parsed.errors.length) {
          setCsvData(null);
        } else {
          setCsvData(parsed.data as string[][]);
        }
      } catch {
        if (!controller.signal.aborted) {
          setCsvData(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void fetchCsvData();

    return () => {
      controller.abort();
    };
  }, [document]);

  if (isLoading) {
    return <LoadingPage />;
  }
  return csvData ? (
    <Card className={csvCardStyle}>
      <table className={csvTableStyle}>
        <thead>
          <tr>
            {(csvData?.[0] || []).map((header, headerIndex) => (
              <th key={headerIndex} className={csvTableHeaderStyle} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {csvData?.slice(1).map((row, rowIndex) => (
            <tr key={rowIndex} className={csvTableRowStyle}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className={csvTableBodyStyle}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  ) : (
    <Card className={csvCardErrorStyle}>
      <Flex justifyContent="center" alignItems="center" flexDirection="column" gap={12}>
        <FileIcon size="xx-large" extension="csv"></FileIcon>
        <Text>Unable to load CSV data.</Text>
      </Flex>
    </Card>
  );
};
